import {
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { KeyboardArrowDownIcon } from 'assets/icons';
import { FC, useCallback } from 'react';
import ja from 'utils/ja';

interface AppSelectProps {
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  options?: Option[];
  valueType?: 'string' | 'number';
  value?: string;
  defaultValue?: string;
  onChange?: (event: SelectChangeEvent<string | number>) => void;
  notSetOpt?: boolean;
}

const AppSelect: FC<AppSelectProps> = ({
  id,
  label,
  required,
  disabled,
  placeholder,
  options = [],
  valueType = 'string',
  value,
  defaultValue,
  onChange,
  error,
  notSetOpt,
}) => {
  const handleChange = useCallback(
    (event: SelectChangeEvent<string | number>) => {
      if (valueType === 'number') {
        event.target.value = Number(event.target.value);
      }
      onChange?.(event);
    },
    [onChange]
  );

  const renderValue = useCallback(
    (selected: string) => {
      if (selected) {
        const label =
          options.find((opt) => opt.value === selected)?.label ?? '';
        return label;
      }
      return <Typography sx={{ color: '#aaabbb' }}>{placeholder}</Typography>;
    },
    [options]
  );

  return (
    <Grid container direction='column' sx={{ gap: { xs: '4px', sm: '10px' } }}>
      {label && (
        <Grid item xs={4} alignItems='center' justifyContent='flex-start'>
          <FormLabel
            htmlFor={id}
            required={required}
            sx={{ color: '#000000de' }}
          >
            {label}
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={8} sx={{ width: '100%', flexDirection: 'column' }}>
        <Select
          id={id}
          name={id}
          value={value != null ? String(value) : undefined}
          defaultValue={defaultValue != null ? String(defaultValue) : undefined}
          displayEmpty
          fullWidth
          disabled={disabled}
          IconComponent={KeyboardArrowDownIcon}
          renderValue={renderValue}
          onChange={handleChange}
          error={!!error}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                overflowY: 'auto',
              },
            },
          }}
          inputProps={{
            sx: {
              padding: '10.63px',
            },
          }}
        >
          {notSetOpt && (
            <MenuItem value='' sx={{ fontSize: '18px' }}>
              {!required ? ja.common.not_set : ja.common.all}
            </MenuItem>
          )}
          {options.map(({ value, label }) => (
            <MenuItem key={value} value={value} sx={{ fontSize: '18px' }}>
              {label}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Grid>
    </Grid>
  );
};

export default AppSelect;
